import React, { useState, useEffect } from 'react';

interface CustomisableLessonPlanPreviewProps {
  onClick?: () => void;
}

const CustomisableLessonPlanPreview: React.FC<CustomisableLessonPlanPreviewProps> = ({ onClick }) => {
  const [activeModule, setActiveModule] = useState(0);
  const [dragTarget, setDragTarget] = useState<number | null>(null);

  // Simulate drag animation
  useEffect(() => {
    const interval = setInterval(() => {
      setDragTarget(2);
      setTimeout(() => setDragTarget(null), 800);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Cycle through modules being highlighted
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveModule((prev) => (prev + 1) % 5);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const lessonModules = [
    { id: 1, title: 'Number Sense', duration: '15 min', type: 'core', color: '#3b82f6', icon: '123' },
    { id: 2, title: 'Place Value Game', duration: '10 min', type: 'practice', color: '#10b981', icon: '🎮' },
    { id: 3, title: 'Word Problems', duration: '12 min', type: 'application', color: '#8b5cf6', icon: '📝' },
    { id: 4, title: 'Quick Quiz', duration: '5 min', type: 'assessment', color: '#f59e0b', icon: '✓' },
    { id: 5, title: 'Extension: Decimals', duration: '8 min', type: 'extension', color: '#06b6d4', icon: '⭐' },
  ];

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'core': return 'Core Lesson';
      case 'practice': return 'Practice';
      case 'application': return 'Application';
      case 'assessment': return 'Assessment';
      case 'extension': return 'Extension';
      default: return type;
    }
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '340px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(139, 92, 246, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Lesson Plan Builder
        </span>
        <div className="flex items-center gap-1">
          <svg className="w-3 h-3" style={{ color: '#8b5cf6' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-[10px]" style={{ color: '#8b5cf6' }}>
            AI-Assisted
          </span>
        </div>
      </div>

      {/* Lesson Info */}
      <div className="p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>
              Year 4 Mathematics
            </div>
            <div className="text-[10px]" style={{ color: '#64748b' }}>
              Week 3: Number & Place Value
            </div>
          </div>
          <div
            className="px-2 py-1 rounded text-[10px] font-medium"
            style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}
          >
            50 min total
          </div>
        </div>
        <div className="flex gap-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, i) => (
            <div
              key={day}
              className="flex-1 text-center py-1 rounded text-[9px]"
              style={{
                background: i === 1 ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                color: i === 1 ? '#3b82f6' : '#64748b',
                fontWeight: i === 1 ? 600 : 400,
              }}
            >
              {day}
            </div>
          ))}
        </div>
      </div>

      {/* Module List */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold" style={{ color: '#64748b' }}>
            LESSON MODULES
          </span>
          <span className="text-[9px]" style={{ color: '#94a3b8' }}>
            Drag to reorder
          </span>
        </div>
        <div className="space-y-2">
          {lessonModules.map((module, index) => (
            <div
              key={module.id}
              className="flex items-center gap-2 p-2 rounded-lg transition-all duration-300"
              style={{
                background: activeModule === index
                  ? `${module.color}15`
                  : dragTarget === index
                    ? 'rgba(139, 92, 246, 0.2)'
                    : 'rgba(255, 255, 255, 0.02)',
                border: activeModule === index
                  ? `1px solid ${module.color}40`
                  : dragTarget === index
                    ? '1px dashed rgba(139, 92, 246, 0.5)'
                    : '1px solid transparent',
                transform: dragTarget === index ? 'scale(1.02)' : 'scale(1)',
              }}
            >
              {/* Drag handle */}
              <div className="flex flex-col gap-0.5 opacity-40">
                <div className="flex gap-0.5">
                  <div className="w-1 h-1 rounded-full" style={{ background: '#64748b' }} />
                  <div className="w-1 h-1 rounded-full" style={{ background: '#64748b' }} />
                </div>
                <div className="flex gap-0.5">
                  <div className="w-1 h-1 rounded-full" style={{ background: '#64748b' }} />
                  <div className="w-1 h-1 rounded-full" style={{ background: '#64748b' }} />
                </div>
              </div>

              {/* Icon */}
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-sm"
                style={{ background: `${module.color}20`, color: module.color }}
              >
                {module.icon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate" style={{ color: '#e2e8f0' }}>
                  {module.title}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px]" style={{ color: module.color }}>
                    {getTypeLabel(module.type)}
                  </span>
                  <span className="text-[9px]" style={{ color: '#64748b' }}>
                    {module.duration}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                <div
                  className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ background: 'rgba(255, 255, 255, 0.05)' }}
                >
                  <svg className="w-3 h-3" style={{ color: '#64748b' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Suggestion */}
      <div className="px-3 pb-3">
        <div
          className="p-2 rounded-lg flex items-start gap-2"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
          }}
        >
          <div
            className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: 'rgba(139, 92, 246, 0.3)' }}
          >
            <svg className="w-3 h-3" style={{ color: '#8b5cf6' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <div className="text-[10px] font-medium" style={{ color: '#8b5cf6' }}>
              AI Suggestion
            </div>
            <div className="text-[10px]" style={{ color: '#94a3b8' }}>
              Based on class data, consider adding a review module for 6 students struggling with place value.
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-center gap-2">
          <div
            className="px-2 py-1 rounded text-[9px] font-medium"
            style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}
          >
            Curriculum Aligned
          </div>
          <div
            className="px-2 py-1 rounded text-[9px] font-medium"
            style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' }}
          >
            Differentiated
          </div>
        </div>
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          5 modules
        </span>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default CustomisableLessonPlanPreview;
